import React, { useState, useEffect } from 'react';
import { Upload, Image as ImageIcon, Plus, Edit2, Trash2, Eye, Download, FileText, Loader2, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { advertisementsApi, eventApi } from '../lib/api';

interface Advertisement {
  id: string;
  title: string;
  description: string;
  image_url: string;
  is_customizable: boolean;
  event_id?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  status: 'active' | 'inactive';
}

const Advertisements = () => {
  const { user } = useAuth();
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAd, setEditingAd] = useState<Advertisement | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    is_customizable: true,
    event_id: '',
    status: 'active' as 'active' | 'inactive'
  });

  useEffect(() => {
    loadAdvertisements();
    if (user?.role === 'admin') {
      loadEvents();
    }
  }, []);

  const loadAdvertisements = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await advertisementsApi.getAll();
      setAdvertisements(data);
    } catch (err: any) {
      console.error('Error loading advertisements:', err);
      setError(err.message || 'Errore nel caricamento delle grafiche');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      const data = await eventApi.getAll();
      setEvents(data);
    } catch (err) {
      console.error('Error loading events:', err);
    }
  };

  const handleOpenModal = (ad?: Advertisement) => {
    if (ad) {
      setEditingAd(ad);
      setFormData({
        title: ad.title,
        description: ad.description,
        image_url: ad.image_url,
        is_customizable: ad.is_customizable,
        event_id: ad.event_id || '',
        status: ad.status
      });
      setPreviewUrl(ad.image_url);
    } else {
      setEditingAd(null);
      setFormData({
        title: '',
        description: '',
        image_url: '',
        is_customizable: true,
        event_id: '',
        status: 'active'
      });
      setPreviewUrl('');
    }
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAd(null);
    setSelectedFile(null);
    setPreviewUrl('');
    setFormData({
      title: '',
      description: '',
      image_url: '',
      is_customizable: true,
      event_id: '',
      status: 'active'
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Per favore seleziona un file immagine');
      return;
    }

    if (file.size > 10485760) {
      alert('Il file deve essere inferiore a 10MB');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile && !formData.image_url && !editingAd) {
      alert('Per favore seleziona un\'immagine');
      return;
    }

    try {
      setUploading(true);
      let imageUrl = formData.image_url;

      if (selectedFile) {
        imageUrl = await advertisementsApi.uploadImage(selectedFile);
      }

      const payload = {
        title: formData.title,
        description: formData.description,
        image_url: imageUrl,
        is_customizable: formData.is_customizable,
        event_id: formData.event_id || null,
        status: formData.status
      };

      if (editingAd) {
        await advertisementsApi.update(editingAd.id, payload);
      } else {
        await advertisementsApi.create(payload);
      }

      handleCloseModal();
      loadAdvertisements();
    } catch (err: any) {
      console.error('Error saving advertisement:', err);
      alert(err.message || 'Errore nel salvataggio della grafica');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa grafica?')) return;

    try {
      await advertisementsApi.delete(id);
      loadAdvertisements();
    } catch (err: any) {
      console.error('Error deleting advertisement:', err);
      alert(err.message || 'Errore nell\'eliminazione della grafica');
    }
  };

  const handleDownload = async (imageUrl: string, title: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/\s+/g, '_')}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading image:', err);
      alert('Errore nel download dell\'immagine');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Caricamento grafiche...</div>
      </div>
    );
  }

  const totalAds = advertisements.length;
  const activeAds = advertisements.filter(ad => ad.status === 'active').length;
  const customizableAds = advertisements.filter(ad => ad.is_customizable).length;
  const linkedToEvents = advertisements.filter(ad => ad.event_id).length;

  const filteredAdvertisements = advertisements.filter(ad => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      ad.title.toLowerCase().includes(searchLower) ||
      ad.description?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="p-6">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Totale Grafiche</p>
                  <p className="text-3xl font-bold text-white mt-1">{totalAds}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <ImageIcon className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Attive</p>
                  <p className="text-3xl font-bold text-white mt-1">{activeAds}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Eye className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Personalizzabili</p>
                  <p className="text-3xl font-bold text-white mt-1">{customizableAds}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Edit2 className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Collegate a Eventi</p>
                  <p className="text-3xl font-bold text-white mt-1">{linkedToEvents}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <FileText className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Cerca grafiche per titolo o descrizione..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-rose-500 focus:border-rose-500"
          />
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={() => handleOpenModal()}
            className="bg-gradient-to-br from-rose-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all flex items-center gap-2 text-sm font-medium shadow-lg"
          >
            <Plus className="h-4 w-4" />
            Nuova Grafica
          </button>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {filteredAdvertisements.length === 0 ? (
        <div className="text-center py-12">
          {searchQuery ? (
            <>
              <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                Nessuna grafica trovata per "{searchQuery}"
              </p>
            </>
          ) : (
            <>
              <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Nessuna grafica disponibile</p>
              {user?.role === 'admin' && (
                <button
                  onClick={() => handleOpenModal()}
                  className="mt-4 text-rose-600 hover:text-rose-700 font-medium"
                >
                  Crea la prima grafica
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAdvertisements.map((ad) => (
            <div key={ad.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative aspect-square bg-gray-100">
                <img
                  src={ad.image_url}
                  alt={ad.title}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => setSelectedImage(ad.image_url)}
                />
                {ad.status === 'inactive' && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-gray-800 text-white text-xs rounded">
                    Inattiva
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">{ad.title}</h3>
                {ad.description && (
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{ad.description}</p>
                )}
                {ad.is_customizable && (
                  <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded mb-3">
                    Personalizzabile
                  </span>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedImage(ad.image_url)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    Visualizza
                  </button>
                  <button
                    onClick={() => handleDownload(ad.image_url, ad.title)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Scarica
                  </button>
                </div>
                {user?.role === 'admin' && (
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleOpenModal(ad)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                    >
                      <Edit2 className="h-4 w-4" />
                      Modifica
                    </button>
                    <button
                      onClick={() => handleDelete(ad.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Elimina
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isModalOpen && user?.role === 'admin' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingAd ? 'Modifica Grafica' : 'Nuova Grafica'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={uploading}
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titolo *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    required
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrizione
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Immagine *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-rose-500 transition-colors">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                      disabled={uploading}
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="text-rose-600 font-medium">Clicca per caricare</span> o trascina qui
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF, WEBP fino a 10MB
                      </p>
                    </label>
                  </div>
                </div>

                {previewUrl && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Anteprima:</p>
                    <img
                      src={previewUrl}
                      alt="Anteprima"
                      className="w-full max-h-64 object-contain rounded-lg border border-gray-200"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Evento (opzionale)
                  </label>
                  <select
                    value={formData.event_id}
                    onChange={(e) => setFormData({ ...formData, event_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    disabled={uploading}
                  >
                    <option value="">Nessun evento specifico</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.name} - {new Date(event.date).toLocaleDateString('it-IT')}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_customizable"
                    checked={formData.is_customizable}
                    onChange={(e) => setFormData({ ...formData, is_customizable: e.target.checked })}
                    className="h-4 w-4 text-rose-600 focus:ring-rose-500 border-gray-300 rounded"
                    disabled={uploading}
                  />
                  <label htmlFor="is_customizable" className="text-sm font-medium text-gray-700">
                    Personalizzabile da Organizer e PR
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stato
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    disabled={uploading}
                  >
                    <option value="active">Attiva</option>
                    <option value="inactive">Inattiva</option>
                  </select>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={uploading}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors flex items-center justify-center gap-2"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Caricamento...
                      </>
                    ) : (
                      editingAd ? 'Salva Modifiche' : 'Crea Grafica'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-4xl max-h-full">
            <img
              src={selectedImage}
              alt="Visualizzazione completa"
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Advertisements;
